import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { getStoredUser } from '@shared/auth/session';

export type RoleTheme = 'citizen' | 'pro';

interface RoleThemeContextValue {
  role: RoleTheme;
  setRole: (role: RoleTheme) => void;
}

export interface RoleThemeProviderProps {
  children: React.ReactNode;
  role?: RoleTheme;
  fallbackRole?: RoleTheme;
  storageKey?: string;
  dataAttribute?: string;
}

const RoleThemeContext = createContext<RoleThemeContextValue | undefined>(undefined);

const resolveRoleTheme = (storageKey: string, fallbackRole: RoleTheme): RoleTheme => {
  const storedAccountType = localStorage.getItem(storageKey);
  if (storedAccountType === 'citizen' || storedAccountType === 'pro') {
    return storedAccountType;
  }

  const user = getStoredUser();
  if (user?.role === 'citizen') {
    return 'citizen';
  }

  if (window.location.pathname.startsWith('/citizen')) {
    return 'citizen';
  }

  return fallbackRole;
};

export const RoleThemeProvider: React.FC<RoleThemeProviderProps> = ({
  children,
  role,
  fallbackRole = 'pro',
  storageKey = 'accountType',
  dataAttribute = 'data-role-theme',
}) => {
  const [currentRole, setCurrentRole] = useState<RoleTheme>(() =>
    role ?? resolveRoleTheme(storageKey, fallbackRole)
  );

  useEffect(() => {
    if (!role) {
      setCurrentRole(resolveRoleTheme(storageKey, fallbackRole));
    }
  }, [fallbackRole, role, storageKey]);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute(dataAttribute, currentRole);
  }, [currentRole, dataAttribute]);

  useEffect(() => {
    if (role) {
      return undefined;
    }

    const refresh = () => setCurrentRole(resolveRoleTheme(storageKey, fallbackRole));
    window.addEventListener('storage', refresh);
    window.addEventListener('authChange', refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('authChange', refresh);
    };
  }, [fallbackRole, role, storageKey]);

  const value = useMemo(
    () => ({
      role: currentRole,
      setRole: setCurrentRole,
    }),
    [currentRole]
  );

  return <RoleThemeContext.Provider value={value}>{children}</RoleThemeContext.Provider>;
};

export const useRoleTheme = () => {
  const context = useContext(RoleThemeContext);
  if (context === undefined) {
    throw new Error('useRoleTheme must be used within a RoleThemeProvider');
  }
  return context;
};

export default RoleThemeContext;
